// Meal type enum
export const MealType = {
  BREAKFAST: 'breakfast',
  LUNCH: 'lunch',
  DINNER: 'dinner',
  SNACK: 'snack',
  DRINK: 'drink',
  OTHER: 'other'
} as const

export type MealTypeType = typeof MealType[keyof typeof MealType]

// Dietary restriction enum
export const DietaryRestriction = {
  VEGETARIAN: 'vegetarian',
  VEGAN: 'vegan',
  GLUTEN_FREE: 'gluten_free',
  DAIRY_FREE: 'dairy_free',
  NUT_ALLERGY: 'nut_allergy',
  SHELLFISH_ALLERGY: 'shellfish_allergy',
  KOSHER: 'kosher',
  HALAL: 'halal',
  NO_PORK: 'no_pork',
  NO_BEEF: 'no_beef',
  LOW_SODIUM: 'low_sodium',
  DIABETIC_FRIENDLY: 'diabetic_friendly',
  OTHER: 'other'
} as const

export type DietaryRestrictionType = typeof DietaryRestriction[keyof typeof DietaryRestriction]

// Meal interfaces
export interface CreateMealDto {
  name: string
  description?: string
  meal_type: MealTypeType
  serving_size: number
  preparation_time_minutes?: number
  cooking_equipment?: string[]
  ingredients?: string[]
  dietary_restrictions?: DietaryRestrictionType[]
  allergens?: string[]
  notes?: string
  is_planned?: boolean
  planned_date?: string // ISO date string
}

export interface UpdateMealDto {
  name?: string
  description?: string
  meal_type?: MealTypeType
  serving_size?: number
  preparation_time_minutes?: number
  cooking_equipment?: string[]
  ingredients?: string[]
  dietary_restrictions?: DietaryRestrictionType[]
  allergens?: string[]
  notes?: string
  is_planned?: boolean
  planned_date?: string // ISO date string
}

export interface MealUser {
  id: string
  email: string
  full_name: string | null
}

export interface MealResponseDto {
  id: string
  trip_id: string
  user_id: string
  name: string
  description: string | null
  meal_type: MealTypeType
  serving_size: number
  preparation_time_minutes: number | null
  cooking_equipment: string[] | null
  ingredients: string[] | null
  dietary_restrictions: DietaryRestrictionType[] | null
  allergens: string[] | null
  notes: string | null
  is_planned: boolean
  planned_date: string | null
  created_at: string
  updated_at: string
  user?: MealUser
}

// Dietary restriction interfaces
export interface CreateDietaryRestrictionDto {
  dietary_restriction: DietaryRestrictionType
  notes?: string
  severity?: 'mild' | 'moderate' | 'severe'
}

export interface UpdateDietaryRestrictionDto {
  notes?: string
  severity?: 'mild' | 'moderate' | 'severe'
}

export interface DietaryRestrictionResponseDto {
  id: string
  trip_id: string
  user_id: string
  dietary_restriction: DietaryRestrictionType
  notes: string | null
  severity: 'mild' | 'moderate' | 'severe'
  created_at: string
  updated_at: string
  user?: MealUser
}

// Simplified trip dietary restriction interface (aggregated format)
export interface TripDietaryRestriction {
  restriction: string  // The actual restriction text from user profile
  affectedUsers: Array<{
    id: string
    email: string
    full_name: string | null
  }>
}

// Per-member dietary profile for a trip. Allergies (free-form, drive meal alerts) are kept
// distinct from dietaryRestrictions (curated diet-type codes, informational).
export interface TripMemberDietaryProfile {
  user: MealUser
  allergies: string[]
  dietaryRestrictions: string[]
}

// Helper interfaces
export interface MealTypeOption {
  value: string
  label: string
}

export interface DietaryRestrictionOption {
  value: string
  label: string
  description?: string
}

// Helper functions
export function getMealTypeLabel(mealType: MealTypeType): string {
  const labels: Record<MealTypeType, string> = {
    [MealType.BREAKFAST]: 'Breakfast',
    [MealType.LUNCH]: 'Lunch',
    [MealType.DINNER]: 'Dinner',
    [MealType.SNACK]: 'Snack',
    [MealType.DRINK]: 'Drink',
    [MealType.OTHER]: 'Other'
  }
  return labels[mealType] || 'Unknown'
}

export function getMealTypeColor(mealType: MealTypeType): string {
  const colors: Record<MealTypeType, string> = {
    [MealType.BREAKFAST]: 'bg-adventure-100 text-adventure-800',
    [MealType.LUNCH]: 'bg-green-100 text-green-800',
    [MealType.DINNER]: 'bg-purple-100 text-purple-800',
    [MealType.SNACK]: 'bg-earth-100 text-earth-800',
    [MealType.DRINK]: 'bg-adventure-100 text-adventure-800',
    [MealType.OTHER]: 'bg-gray-100 text-gray-800'
  }
  return colors[mealType] || 'bg-gray-100 text-gray-800'
}

export function getDietaryRestrictionLabel(restriction: DietaryRestrictionType): string {
  const labels: Record<DietaryRestrictionType, string> = {
    [DietaryRestriction.VEGETARIAN]: 'Vegetarian',
    [DietaryRestriction.VEGAN]: 'Vegan',
    [DietaryRestriction.GLUTEN_FREE]: 'Gluten-Free',
    [DietaryRestriction.DAIRY_FREE]: 'Dairy-Free',
    [DietaryRestriction.NUT_ALLERGY]: 'Nut Allergy',
    [DietaryRestriction.SHELLFISH_ALLERGY]: 'Shellfish Allergy',
    [DietaryRestriction.KOSHER]: 'Kosher',
    [DietaryRestriction.HALAL]: 'Halal',
    [DietaryRestriction.NO_PORK]: 'No Pork',
    [DietaryRestriction.NO_BEEF]: 'No Beef',
    [DietaryRestriction.LOW_SODIUM]: 'Low Sodium',
    [DietaryRestriction.DIABETIC_FRIENDLY]: 'Diabetic Friendly',
    [DietaryRestriction.OTHER]: 'Other'
  }
  return labels[restriction] || 'Unknown'
}

/**
 * Gets the display text for a dietary restriction.
 * If the restriction type is "other" and has notes, returns the notes (the actual restriction text).
 * Otherwise, tries to get the formatted label for the restriction type.
 * If the restriction doesn't match an enum, returns the restriction text as-is (for free-form restrictions).
 */
export function getDietaryRestrictionDisplayText(restriction: {
  dietary_restriction: string
  notes: string | null
}): string {
  // If it's "other" type and has notes, show the notes (the actual restriction text)
  if (restriction.dietary_restriction === 'other' && restriction.notes) {
    return restriction.notes
  }
  
  // Try to get the formatted label for the restriction type
  // If it doesn't match an enum, getDietaryRestrictionLabel will return 'Unknown'
  const label = getDietaryRestrictionLabel(restriction.dietary_restriction as DietaryRestrictionType)
  
  // If we got 'Unknown', it means the restriction text doesn't match an enum
  // In that case, return the restriction text as-is (for free-form restrictions from user profiles)
  if (label === 'Unknown') {
    // Capitalize first letter for better display
    return restriction.dietary_restriction.charAt(0).toUpperCase() + restriction.dietary_restriction.slice(1)
  }
  
  return label
}

export function getDietaryRestrictionColor(restriction: DietaryRestrictionType): string {
  const colors: Record<DietaryRestrictionType, string> = {
    [DietaryRestriction.VEGETARIAN]: 'bg-green-100 text-green-800',
    [DietaryRestriction.VEGAN]: 'bg-emerald-100 text-emerald-800',
    [DietaryRestriction.GLUTEN_FREE]: 'bg-amber-100 text-amber-800',
    [DietaryRestriction.DAIRY_FREE]: 'bg-adventure-100 text-adventure-800',
    [DietaryRestriction.NUT_ALLERGY]: 'bg-red-100 text-red-800',
    [DietaryRestriction.SHELLFISH_ALLERGY]: 'bg-red-100 text-red-800',
    [DietaryRestriction.KOSHER]: 'bg-purple-100 text-purple-800',
    [DietaryRestriction.HALAL]: 'bg-indigo-100 text-indigo-800',
    [DietaryRestriction.NO_PORK]: 'bg-pink-100 text-pink-800',
    [DietaryRestriction.NO_BEEF]: 'bg-pink-100 text-pink-800',
    [DietaryRestriction.LOW_SODIUM]: 'bg-cyan-100 text-cyan-800',
    [DietaryRestriction.DIABETIC_FRIENDLY]: 'bg-teal-100 text-teal-800',
    [DietaryRestriction.OTHER]: 'bg-gray-100 text-gray-800'
  }
  return colors[restriction] || 'bg-gray-100 text-gray-800'
}
